'use client'

import { useState } from 'react'
import Link from 'next/link'
import type { Form as FormType } from '@payloadcms/plugin-form-builder/types'
import { Button } from '@/components/ui/button'
import { EnquiryModal, type EnquiryAgent, type EnquiryIntent, INTENT_LABELS } from './EnquiryModal'

interface PropertyCtaButtonsProps {
  /** FeedBridge listing_vertical — 'rental' gets Apply / Book An Inspection / Ask A Question */
  vertical: string
  /** 'row' = Figma `Frame 499` 4022:84658 — horizontal, gap/gap-2 8px, at tablet/desktop
   * (confirmed at 768 `4022:107263`). At mobile (390), the 3-CTA rental case
   * (`4086:155242`) stacks full-width single-column instead of wrapping; the 2-CTA
   * sale case still renders as a single row at every width.
   * 'stack' is retained for the legacy Ask A Question band. */
  layout?: 'row' | 'stack'
  /**
   * Sold / leased frames (4084:153204, 4086:156448, 4088:161231, 4088:163112)
   * keep a single PRIMARY "Ask a Question" button under the price.
   */
  questionOnly?: boolean
  /** e.g. "10 Fairweather Avenue, Suburbville" — modal header + enquiry subject */
  addressShort: string
  form: FormType | null
  agents: EnquiryAgent[]
  agentEmails: string[]
  /** When set, "Make An Offer" links into the stepped /offer-to-purchase journey
   *  (address pre-filled, property photo in the right column) instead of the modal. */
  offerHref?: string
  /** When set, "Apply" opens the 2Apply (Inspect Real Estate) application form
   *  in a new tab instead of the modal — matching the old WP site's
   *  epl-inspect-real-estate plugin behaviour. */
  applyHref?: string
  /** When set, "Book An Inspection" opens the Inspect Real Estate booking page
   *  in a new tab instead of the modal — same plugin as applyHref. */
  inspectionHref?: string
}

/**
 * Property enquiry CTA buttons + shared modal.
 * Sale: [Make An Offer primary] [Ask A Question outline]
 * Rental: [Apply primary] [Book An Inspection outline] [Ask A Question outline]
 * Every button opens the enquiry modal pre-scoped with its intent.
 *
 * Buttons are the canonical <Button> — Figma 4022:84575 measures the CTA row at 36px
 * tall (Button Regular = size "default"), primary = type primary (accent), the rest =
 * type outline.
 */
export function PropertyCtaButtons({
  vertical,
  layout = 'row',
  questionOnly = false,
  addressShort,
  form,
  agents,
  agentEmails,
  offerHref,
  applyHref,
  inspectionHref,
}: PropertyCtaButtonsProps) {
  const [openIntent, setOpenIntent] = useState<EnquiryIntent | null>(null)

  const intents: { intent: EnquiryIntent; primary: boolean }[] = questionOnly
    ? [{ intent: 'question', primary: true }]
    : vertical === 'rental'
      ? [
          { intent: 'apply', primary: true },
          { intent: 'inspection', primary: false },
          { intent: 'question', primary: false },
        ]
      : [
          { intent: 'offer', primary: true },
          { intent: 'question', primary: false },
        ]

  return (
    <>
      <div
        className={
          layout === 'stack'
            ? 'flex flex-col items-start gap-3'
            : 'flex flex-col items-stretch gap-2 sm:flex-row sm:items-center'
        }
      >
        {intents.map(({ intent, primary }) => {
          const externalHref =
            intent === 'apply' ? applyHref : intent === 'inspection' ? inspectionHref : undefined
          return intent === 'offer' && offerHref ? (
            <Button
              key={intent}
              asChild
              variant={primary ? 'default' : 'outline'}
              className="w-full sm:w-auto"
            >
              <Link href={offerHref}>{INTENT_LABELS[intent]}</Link>
            </Button>
          ) : externalHref ? (
            <Button
              key={intent}
              asChild
              variant={primary ? 'default' : 'outline'}
              className="w-full sm:w-auto"
            >
              <a href={externalHref} target="_blank" rel="noopener noreferrer">
                {INTENT_LABELS[intent]}
              </a>
            </Button>
          ) : (
            <Button
              key={intent}
              type="button"
              variant={primary ? 'default' : 'outline'}
              onClick={() => setOpenIntent(intent)}
              className="w-full sm:w-auto"
            >
              {INTENT_LABELS[intent]}
            </Button>
          )
        })}
      </div>

      <EnquiryModal
        open={openIntent !== null}
        intent={openIntent || 'question'}
        onClose={() => setOpenIntent(null)}
        addressShort={addressShort}
        form={form}
        agents={agents}
        agentEmails={agentEmails}
        vertical={vertical}
      />
    </>
  )
}
